import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";
import { getErrorDetails } from "../../../app/appUtils";
import AppButton from "../../../components/ui/AppButton";
import AppInputField from "../../../components/ui/AppInputField";
import { Spinner } from "../../../components/ui/common";
import { Modal } from "../../../components/ui/Modal";
import SuccessView from "../../../components/ui/SuccessView";
import { useToast } from "../../../hooks/useToast";
import CompanyDropdownMenu from "../../company/components/CompanyDropdownMenu";
import { PlanConfig } from "../@types";
import {
  useCreatePlanConfigMutation,
  useUpdatePlanConfigMutation,
} from "../subscriptionApiSlice";

export const planFormSchema = z.object({
  companyId: z.string().nonempty("Company id is required"),
  companyName: z.string().nonempty("Company name is required"),
  monthlyPrice: z.number().min(100, "Monthly price must be greater than 100"),
  yearlyPrice: z.number().min(100, "Yearly price must be greater than 100"),
  maxYearlyVehicles: z
    .number()
    .min(1, "Max yearly vehicles must be greater than 0"),
  maxMonthlyVehicles: z
    .number()
    .min(1, "Max monthly vehicles must be greater than 0"),
  quarterlyPrice: z
    .number()
    .min(100, "Quarterly price must be greater than 100"),
  maxQuarterlyVehicles: z
    .number()
    .min(1, "Max quarterly vehicles must be greater than 0"),
});

export type PlanFormData = z.infer<typeof planFormSchema>;

interface PlanModalProps {
  isOpen: boolean;
  onClose: () => void;
  plan?: PlanConfig | null;
  mode?: "add" | "edit";
}

const PlanConfigModal: React.FC<PlanModalProps> = ({
  isOpen,
  onClose,
  plan = null,
  mode = plan ? "edit" : "add",
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const toast = useToast();
  const isEditMode = mode === "edit" && plan;

  const [
    createPlan,
    {
      isLoading: isCreating,
      isSuccess: createSuccess,
      isError: createError,
      error: createErrorData,
      reset: resetCreate,
    },
  ] = useCreatePlanConfigMutation();

  const [
    updatePlan,
    {
      isLoading: isUpdating,
      isSuccess: updateSuccess,
      isError: updateError,
      error: updateErrorData,
      reset: resetUpdate,
    },
  ] = useUpdatePlanConfigMutation();

  // Derived states for cleaner code
  const isLoading = isCreating || isUpdating;
  const isSuccess = createSuccess || updateSuccess;
  const isError = createError || updateError;
  const error = createErrorData || updateErrorData;

  const {
    control,
    handleSubmit,
    formState: { errors, isDirty },
    watch,
    setValue,
    reset,
  } = useForm<PlanFormData>({
    resolver: zodResolver(planFormSchema),
    mode: "onChange",
    defaultValues: {
      companyId: "",
      companyName: "",
      monthlyPrice: 0,
      yearlyPrice: 0,
      maxYearlyVehicles: 0,
      maxMonthlyVehicles: 0,
      quarterlyPrice: 0,
      maxQuarterlyVehicles: 0,
    },
  });

  // Populate form with plan data when editing
  useEffect(() => {
    if (isEditMode && plan) {
      reset({
        companyId: plan.companyId || "",
        companyName: plan.companyName || "",
        monthlyPrice: plan.monthlyPrice || 0,
        yearlyPrice: plan.yearlyPrice || 0,
        maxYearlyVehicles: plan.maxYearlyVehicles || 0,
        maxMonthlyVehicles: plan.maxMonthlyVehicles || 0,
        quarterlyPrice: plan.quarterlyPrice || 0,
        maxQuarterlyVehicles: plan.maxQuarterlyVehicles || 0,
      });
    } else if (!isEditMode) {
      // Reset to empty when switching to add mode
      reset({
        companyId: "",
        companyName: "",
        monthlyPrice: 0,
        yearlyPrice: 0,
        maxYearlyVehicles: 0,
        maxMonthlyVehicles: 0,
        quarterlyPrice: 0,
        maxQuarterlyVehicles: 0,
      });
    }
  }, [isEditMode, plan, reset]);

  const onSubmit = async (data: PlanFormData) => {
    if (isSubmitting || isLoading) return;

    // Prevent submission if editing and no changes were made
    if (isEditMode && !isDirty) {
      toast.warning({
        title: "No Changes",
        message: "No changes were made to update.",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const payload = {
        companyId: data.companyId,
        companyName: data.companyName,
        monthlyPrice: data.monthlyPrice,
        maxMonthlyVehicles: data.maxMonthlyVehicles,
        quarterlyPrice: data.quarterlyPrice,
        maxQuarterlyVehicles: data.maxQuarterlyVehicles,
        yearlyPrice: data.yearlyPrice,
        maxYearlyVehicles: data.maxYearlyVehicles,
      };

      if (isEditMode && plan?.id) {
        await updatePlan({
          id: plan.id,
          ...payload,
        }).unwrap();
      } else {
        await createPlan(payload).unwrap();
      }

      setShowSuccess(true);
    } catch (err) {
      console.error(`${isEditMode ? "Update" : "Create"} error:`, err);
      const errorMessage =
        getErrorDetails(err).message ||
        `Failed to ${isEditMode ? "update" : "add"} plan. Please try again.`;

      toast.error({
        title: "Error",
        message: errorMessage,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    if (isSuccess && !showSuccess) {
      toast.success({
        title: "Success",
        message: `Plan ${isEditMode ? "updated" : "added"} successfully`,
      });
      setShowSuccess(true);
    }

    if (isError) {
      const errorMessage =
        getErrorDetails(error).message ||
        `Failed to ${isEditMode ? "update" : "add"} plan. Please try again.`;

      toast.error({
        title: "Error",
        message: errorMessage,
      });
    }
  }, [isSuccess, isError, error, toast, isEditMode, showSuccess]);

  const handleCloseModal = () => {
    setShowSuccess(false);
    reset();
    resetCreate();
    resetUpdate();
    onClose();
  };

  const renderSuccessView = () => {
    if (showSuccess) {
      return (
        <SuccessView
          handleCloseModal={handleCloseModal}
          heading=""
          message={`Plan has been ${
            isEditMode ? "updated" : "added"
          } successfully.`}
        />
      );
    }
    return null;
  };

  const modalTitle = showSuccess ? "" : `${isEditMode ? "Edit" : "Add"} plan`;
  const submitButtonText = isEditMode ? "Update plan" : "+ Add plan";

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleCloseModal}
      title={modalTitle}
      className="max-w-full sm:max-w-[80vw] lg:max-w-[45vw] w-full bg-white pb-8"
    >
      {isSuccess ? (
        renderSuccessView()
      ) : (
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6 p-6">
          <div className="">
            <Controller
              control={control}
              name="companyId"
              render={({ field }) => (
                <CompanyDropdownMenu
                  value={watch("companyId")}
                  onChange={(value) => {
                    setValue("companyId", value?.value || "", {
                      shouldValidate: true,
                    });

                    setValue("companyName", value?.label || "", {
                      shouldValidate: true,
                    });
                  }}
                  className="bg-[#F6F6F6] border-[#F6F6F6] h-[48px] mb-6"
                  error={errors.companyId?.message}
                  placeholder="Select a company"
                />
              )}
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <Controller
              control={control}
              name="monthlyPrice"
              render={({ field }) => (
                <AppInputField
                  label="Monthly price"
                  type="number"
                  error={errors.monthlyPrice?.message}
                  {...field}
                  onChange={(e) => field.onChange(e.target.valueAsNumber)}
                  className="bg-[#F6F6F6] border-[#F6F6F6] h-[48px]"
                  placeholder="Enter monthly price"
                />
              )}
            />

            <Controller
              control={control}
              name="maxMonthlyVehicles"
              render={({ field }) => (
                <AppInputField
                  label="Max monthly vehicles"
                  type="number"
                  error={errors.maxMonthlyVehicles?.message}
                  {...field}
                  onChange={(e) => field.onChange(e.target.valueAsNumber)}
                  className="bg-[#F6F6F6] border-[#F6F6F6] h-[48px]"
                  placeholder="Enter monthly price"
                />
              )}
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <Controller
              control={control}
              name="quarterlyPrice"
              render={({ field }) => (
                <AppInputField
                  label="Quarterly price"
                  type="number"
                  error={errors.quarterlyPrice?.message}
                  {...field}
                  onChange={(e) => field.onChange(e.target.valueAsNumber)}
                  className="bg-[#F6F6F6] border-[#F6F6F6] h-[48px]"
                  placeholder="Enter quarterly price"
                />
              )}
            />

            <Controller
              control={control}
              name="maxQuarterlyVehicles"
              render={({ field }) => (
                <AppInputField
                  label="Max quarterly vehicles"
                  type="number"
                  error={errors.maxQuarterlyVehicles?.message}
                  {...field}
                  onChange={(e) => field.onChange(e.target.valueAsNumber)}
                  className="bg-[#F6F6F6] border-[#F6F6F6] h-[48px]"
                  placeholder="Enter quarterly price"
                />
              )}
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <Controller
              control={control}
              name="yearlyPrice"
              render={({ field }) => (
                <AppInputField
                  label="Yearly price"
                  type="number"
                  error={errors.yearlyPrice?.message}
                  {...field}
                  onChange={(e) => field.onChange(e.target.valueAsNumber)}
                  className="bg-[#F6F6F6] border-[#F6F6F6] h-[48px]"
                  placeholder="Enter yearly price"
                />
              )}
            />

            <Controller
              control={control}
              name="maxYearlyVehicles"
              render={({ field }) => (
                <AppInputField
                  label="Max yearly vehicles"
                  type="number"
                  error={errors.maxYearlyVehicles?.message}
                  {...field}
                  onChange={(e) => field.onChange(e.target.valueAsNumber)}
                  className="bg-[#F6F6F6] border-[#F6F6F6] h-[48px]"
                  placeholder="Enter yearly price"
                />
              )}
            />
          </div>

          <div className="flex justify-center items-center space-x-4 text-[15px]">
            <AppButton
              type="button"
              onClick={handleCloseModal}
              disabled={isSubmitting || isLoading}
              className="px-6 py-2 bg-[#F6F6F6] rounded-md text-hColor hover:bg-gray-50 transition-colors text-[15px] disabled:opacity-50"
            >
              Cancel
            </AppButton>

            <AppButton
              type="submit"
              disabled={isSubmitting || isLoading}
              className="px-6 py-2 bg-primary text-white rounded-md hover:bg-primary/75 transition-colors disabled:opacity-50 text-[15px]"
            >
              {isSubmitting || isLoading ? <Spinner /> : submitButtonText}
            </AppButton>
          </div>
        </form>
      )}
    </Modal>
  );
};

export default PlanConfigModal;
